/**
 * Card ID Generation Utility
 *
 * Generates unique, consistent card IDs for booking cards.
 * This is the single source of truth for card ID generation across the application.
 *
 * Format: `{date}:{entityType}:{entityId}`
 * - date: yyyy-MM-dd format
 * - entityType: "employee" | "customer" | "unknown"
 * - entityId: numeric ID of the entity
 *
 * Examples:
 * - "2024-12-01:employee:123"
 * - "2024-12-01:customer:456"
 * - "2024-12-01:unknown"
 */

import type { IServiceCustomer, IServiceEmployee } from "@/services/types";
import { formatDateKey } from "@/utils/date";

/**
 * Generate a unique card ID for a booking card.
 *
 * Card IDs follow the format: `{dateStr}:{entityType}:{entityId}`
 *
 * Priority:
 * 1. If employee is provided, uses employee ID
 * 2. If only customer is provided, uses customer ID
 * 3. Otherwise, generates an "unknown" ID
 *
 * @param date - The date for the booking card
 * @param employee - Optional employee entity
 * @param customer - Optional customer entity
 * @returns Unique card ID string
 */
export function generateCardId(
  date: Date,
  employee?: IServiceEmployee | null,
  customer?: IServiceCustomer | null,
): string {
  const dateStr = formatDateKey(date);
  if (employee) {
    return `${dateStr}:employee:${employee.id}`;
  }
  if (customer) {
    return `${dateStr}:customer:${customer.id}`;
  }
  return `${dateStr}:unknown`;
}

/**
 * Parse a card ID back into its components.
 *
 * @param cardId - The card ID to parse
 * @returns Parsed components or null if invalid format
 */
export function parseCardId(
  cardId: string,
): { date: string; entityType: string; entityId: number | null } | null {
  const parts = cardId.split(":");
  if (parts.length < 2) return null;

  const [date, entityType, entityIdStr] = parts;

  if (!date || !entityType) return null;

  return {
    date,
    entityType,
    entityId: entityIdStr ? Number(entityIdStr) : null,
  };
}

/**
 * Extract the date string from a card ID.
 *
 * @param cardId - The card ID
 * @returns The date portion (yyyy-MM-dd) or null if invalid
 */
export function getDateFromCardId(cardId: string): string | null {
  const parsed = parseCardId(cardId);
  return parsed?.date ?? null;
}

/**
 * Check if a card ID belongs to an employee.
 *
 * @param cardId - The card ID to check
 * @returns True if the card belongs to an employee
 */
export function isEmployeeCard(cardId: string): boolean {
  return cardId.includes(":employee:");
}

/**
 * Check if a card ID belongs to a customer.
 *
 * @param cardId - The card ID to check
 * @returns True if the card belongs to a customer
 */
export function isCustomerCard(cardId: string): boolean {
  return cardId.includes(":customer:");
}
